import { useCallback, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

const HLS_MIME_TYPE = 'application/vnd.apple.mpegurl';

const waitForEvent = (target, eventName) =>
  new Promise((resolve) => {
    const handleEvent = () => {
      target.removeEventListener(eventName, handleEvent);
      resolve();
    };

    target.addEventListener(eventName, handleEvent);
  });

const useLazyHlsPlayback = (src) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const initializedRef = useRef(false);
  const initializationPromiseRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);

  const destroyPlayback = useCallback(() => {
    initializationPromiseRef.current = null;
    initializedRef.current = false;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;

    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
  }, []);

  useEffect(() => {
    initializedRef.current = false;
    initializationPromiseRef.current = null;
    setIsPlaying(false);
    setIsWaiting(false);

    return () => {
      destroyPlayback();
    };
  }, [destroyPlayback, src]);

  const initializePlayback = useCallback(async () => {
    const video = videoRef.current;

    if (!video) {
      throw new Error('Video element is not available');
    }

    if (initializedRef.current) {
      return;
    }

    if (initializationPromiseRef.current) {
      return initializationPromiseRef.current;
    }

    const initializationPromise = (async () => {
      if (Hls.isSupported()) {
        await new Promise((resolve, reject) => {
          const hls = new Hls();
          hlsRef.current = hls;

          const handleManifestParsed = () => {
            hls.off(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
            hls.off(Hls.Events.ERROR, handleError);
            initializedRef.current = true;
            resolve();
          };

          const handleError = (_, data) => {
            if (!data?.fatal) {
              return;
            }

            hls.off(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
            hls.off(Hls.Events.ERROR, handleError);

            if (hlsRef.current === hls) {
              hlsRef.current = null;
            }

            hls.destroy();
            reject(new Error(data.type || 'Failed to initialize HLS playback'));
          };

          hls.on(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
          hls.on(Hls.Events.ERROR, handleError);
          hls.loadSource(src);
          hls.attachMedia(video);
        });

        return;
      }

      if (video.canPlayType(HLS_MIME_TYPE)) {
        if (!video.src || video.src !== src) {
          const metadataReady = video.readyState >= 1 ? Promise.resolve() : waitForEvent(video, 'loadedmetadata');
          video.src = src;
          video.load();
          await metadataReady;
        }

        initializedRef.current = true;
        return;
      }

      throw new Error("This browser doesn't support HLS playback");
    })();

    initializationPromiseRef.current = initializationPromise;

    try {
      await initializationPromise;
    } finally {
      initializationPromiseRef.current = null;
    }
  }, [src]);

  const togglePlay = useCallback(async () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (!video.paused && !video.ended) {
      setIsWaiting(false);
      video.pause();
      return;
    }

    setIsWaiting(true);

    try {
      await initializePlayback();
      await video.play();
    } catch (error) {
      setIsWaiting(false);
      console.error('Unable to start video playback', error);
    }
  }, [initializePlayback]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setIsWaiting(false);
  }, []);

  const handlePlaying = useCallback(() => {
    setIsPlaying(true);
    setIsWaiting(false);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    setIsWaiting(false);
  }, []);

  const handleWaiting = useCallback(() => {
    setIsWaiting(true);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setIsWaiting(false);
  }, []);

  return {
    videoRef,
    isPlaying,
    isWaiting,
    togglePlay,
    handlePlay,
    handlePlaying,
    handlePause,
    handleWaiting,
    handleEnded,
  };
};

export default useLazyHlsPlayback;
